import { Many, ListIteratee } from 'lodash';
import orderBy from 'lodash/orderBy';

import { JsonSchemaBasicObject, JsonSchemaType } from '@evidentid/json-schema/interfaces/JsonSchema';
import { CustomProperty } from '@evidentid/tprm-portal-lib/models/dashboard';

import { entitySchemas } from './constants';
import { getCustomPropertySchema, getExceptionsSchema, getProducerListSchema } from './helpers';

const commonPropertiesOrder = [
    'contactEmail',
    'contactName',
    'contactPhoneNumber',
    'address',
    'insuredFields',
    'exceptions',
];
const commonRequiredFields = [
    'contactEmail',
    'insuredFields',
    'exceptions',
];

export function buildSingleEntityImportJsonSchema(
    customProperties: CustomProperty[],
    requirementTypes: string[] = [],
    customPropertiesSortingIteratees: Many<ListIteratee<CustomProperty>> = (_, index) => index,
    customPropertyOrders: Parameters<typeof orderBy>[2] = [],
): JsonSchemaBasicObject {
    return {
        type: JsonSchemaType.object,
        properties: {
            insuredName: {
                type: JsonSchemaType.object,
                title: 'Name(s)',
                properties: {
                    displayName: entitySchemas.displayName,
                    legalName: entitySchemas.legalName,
                    doingBusinessAs: entitySchemas.doingBusinessAs,
                },
                required: [ 'displayName' ],
                propertiesOrder: [ 'displayName', 'legalName', 'doingBusinessAs' ],
            },
            contactEmail: entitySchemas.contactEmail,
            contactName: entitySchemas.contactName,
            contactPhoneNumber: entitySchemas.contactPhoneNumber,
            address: entitySchemas.address,
            insuredFields: getCustomPropertySchema(
                customProperties, customPropertiesSortingIteratees, customPropertyOrders,
            ),
            exceptions: getExceptionsSchema(requirementTypes),
        },
        propertiesOrder: [ 'insuredName', ...commonPropertiesOrder ],
        required: [ 'insuredName', ...commonRequiredFields ],
    };
}

export function buildBulkEntityImportJsonSchema(
    customProperties: CustomProperty[],
    requirementTypes: string[] = [],
    producerCoverageTypes: string[] | null = null,
    producerCoverageTypeLabels: Record<string, string> = {},
): JsonSchemaBasicObject {
    return {
        type: JsonSchemaType.object,
        properties: {
            // Mandatory as a column, optional per row: a blank cell means "create this entity".
            id: entitySchemas.id,
            displayName: entitySchemas.displayName,
            legalName: entitySchemas.legalName,
            doingBusinessAs: entitySchemas.doingBusinessAs,
            contactEmail: entitySchemas.contactEmail,
            contactName: entitySchemas.contactName,
            contactPhoneNumber: entitySchemas.contactPhoneNumber,
            address: entitySchemas.address,
            ...(producerCoverageTypes && {
                producerList: getProducerListSchema(producerCoverageTypes, producerCoverageTypeLabels),
            }),
            insuredFields: getCustomPropertySchema(customProperties),
            exceptions: getExceptionsSchema(requirementTypes),
        },
        propertiesOrder: [
            'id', 'displayName', 'legalName', 'doingBusinessAs', 'contactEmail', 'contactName',
            'contactPhoneNumber', 'address', ...(producerCoverageTypes ? [ 'producerList' ] : []),
            'insuredFields', 'exceptions',
        ],
        required: [ 'displayName', ...commonRequiredFields ],
    };
}
